// src/components/ReportInsights/ReportInsights.js
import { useMemo, useState } from "react";
import { ChevronIcon } from "../../assets/icons/icons";
import { useStrategicSuggestions } from "../../contexts/StrategicSuggestionsContext";
import "./style.css";

const AttributeInsight = ({ attr }) => {
  return (
    <li key={attr.attribute_id} className="insight-list-column">
      <div className="attribute-label-row">
        <span className="attribute-label-prefix">Attribute:</span>
        <span className="attribute-label-main">{attr.label}</span>
        <span className="attribute-score-label">Score:</span>
        <span className="attribute-score" style={{ color: attr.color }}>
          {attr.meanPct}
        </span>
      </div>
      <div className="insight-subitem">
        {attr.suggestion && (
          <>
            <div>
              <h3>{attr.suggestion.title}</h3>
              <p dangerouslySetInnerHTML={{ __html: attr.suggestion.description }} />
            </div>
            {attr.links && (
              <div className="suggestion-links">
                <h4>Resource Partners:</h4>
                <ul>
                  {attr.links.map((link, index) => (
                    <li key={index}>
                      <a href={link.url} target="_blank" rel="noopener noreferrer">
                        {link.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </li>
  );
}

export default function ReportInsights({ reportData }) {
  const [expandedKey, setExpandedKey] = useState(null);
  const { categories } = reportData;
  const pillars = categories.flatMap((cat) => cat.pillars);
  const attributes = pillars.flatMap((p) => p.attributes);
  const { useConfig } = require("../../contexts/ConfigContext");
  const { config } = useConfig();
  const { strategicSuggestions } = useStrategicSuggestions();
  const { byAttrId: suggestionsMap } = strategicSuggestions;

  // compute attribute means
  const attributeMeans = useMemo(
    () =>
      attributes.filter(
        (attr, idx, arr) =>
          arr.findIndex(a => a.attribute_id === attr.attribute_id) === idx
      ).map((attr) => {
        const suggestion = suggestionsMap[attr.attribute_id] || null;
        const links = suggestion && suggestion.linksMap ? suggestion.linksMap['For Company'] || [] : [];
        return {
          attribute_id: attr.attribute_id,
          label: attr.attribute_name,
          meanPct: attr.avg_score,
          grade: attr.avg_grade,
          color: config.gradeScale[attr.avg_grade].color || "black",
          suggestion: suggestion,
          links: links,
        };
      }),
    [attributes, config.gradeScale, suggestionsMap]
  );

  // top-3 attributes
  const topAttributes = useMemo(
    () =>
      [...attributeMeans]
        .filter(a =>
          a.meanPct != null
          //  && a.grade !== 'D' && a.grade !== 'S' // Commented for potential future requirements
        )
        .sort((a, b) => b.meanPct - a.meanPct)
        .slice(0, 3),
    [attributeMeans]
  );

  // bottom-3 attributes
  const bottomAttributes = useMemo(
    () =>
      [...attributeMeans]
        .filter(a =>
          a.meanPct != null &&
          a.meanPct < 100 && // Added
          //  a.grade !== 'A' && // Commented for potential future requirements
          !topAttributes.some(ta => a.attribute_id === ta.attribute_id)
        )
        .sort((a, b) => a.meanPct - b.meanPct)
        .slice(0, 3),
    [attributeMeans, topAttributes]
  );

  console.log("All Attribute Means (Initial Data):", attributeMeans);
  console.log("Highest-Scoring Attributes (Top 3):", topAttributes);
  console.log("Lowest-Scoring Attributes (Bottom 3):", bottomAttributes);

  // — compute suggestions for every attribute once (match by attribute_id *only*)
  // const suggestionsMap = useMemo(() => {
  //   const map = {};
  //   attributeMeans.forEach(({ attribute_id, meta }) => {
  //     // grab *all* modules that mention this attribute_id
  //     const all = learningModulesList
  //       .filter(
  //         (mod) =>
  //           Array.isArray(mod.strategic_suggestions) &&
  //           mod.strategic_suggestions.some(
  //             (s) => s.attribute_id === attribute_id
  //           )
  //       )
  //       .map((mod) => ({
  //         text: mod.credit_title,
  //         link: `/learning-modules?moduleId=${encodeURIComponent(mod.sku)}`,
  //         sku: mod.sku,
  //       }));

  //     map[attribute_id] = {
  //       allSuggestions: all,
  //       previewSuggestions: pickRandom(all, 3),
  //     };
  //   });
  //   return map;
  // }, [attributeMeans]);


  // lowest-3 questions
  // const lowestQuestions = useMemo(() => {
  //   return safeQuestionScores
  //     .map(({ question_id, scores }) => {
  //       if (!scores.length) return null;
  //       const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  //       const pct = Math.round(avg * 100);
  //       const meta = safeQuestionsMeta.find((q) => q.id === question_id) || {};
  //       return {
  //         question_id,
  //         text: meta.text || `Question ${question_id}`,
  //         pct,
  //       };
  //     })
  //     .filter(Boolean)
  //     .sort((a, b) => a.pct - b.pct)
  //     .slice(0, 3);
  // }, [safeQuestionScores, safeQuestionsMeta]);

  // // top-3 questions
  // const highestQuestions = useMemo(() => {
  //   return safeQuestionScores
  //     .map(({ question_id, scores }) => {
  //       if (!scores.length) return null;
  //       const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  //       const pct = Math.round(avg * 100);
  //       const meta = safeQuestionsMeta.find((q) => q.id === question_id) || {};
  //       return {
  //         question_id,
  //         text: meta.text || `Question ${question_id}`,
  //         pct,
  //       };
  //     })
  //     .filter(Boolean)
  //     .sort((a, b) => b.pct - a.pct)
  //     .slice(0, 3);
  // }, [safeQuestionScores, safeQuestionsMeta]);

  const sections = [
    {
      key: "topAttrs",
      title: "Highest-Scoring Attributes",
      content: (
        <ul className="insight-list">
          {topAttributes.map((a) => (
            <AttributeInsight key={a.attribute_id} attr={a} />
          ))}
        </ul>
      ),
    },

    {
      key: "bottomAttrs",
      title: "Lowest-Scoring Attributes",
      content: (
        <ul className="insight-list">
          {bottomAttributes.map((a) => (
            <AttributeInsight key={a.attribute_id} attr={a} />
          ))}
        </ul>
      ),
    },
  ];

  return (
    <div className="report-insights-section">
      <h2>Report Insights</h2>
      <p className="report-insights-subtitle">
        Quick insights into your report data
      </p>
      <div className="report-insights-container">
        {sections.map(({ key, title, content }) => (
          <div key={key} className="insight-item">
            <div
              className="insight-header"
              onClick={() =>
                setExpandedKey((prev) =>
                  prev === key ? null : key
                )
              }
            >
              <span>{title}</span>
              <span
                className={`chevron-${expandedKey === key ? "down" : "up"
                  }`}
              >
                <ChevronIcon />
              </span>
            </div>
            {expandedKey === key && (
              <div className="insight-body">{content}</div>
            )}
          </div>
        ))}

        {/* <StrategicSuggestionModal
          isOpen={openSugAttr != null}
          closeModal={() => setOpenSugAttr(null)}
          previewSuggestions={
            suggestionsMap[openSugAttr]?.previewSuggestions || []
          }
          allSuggestions={
            suggestionsMap[openSugAttr]?.allSuggestions || []
          }
        /> */}
      </div>
    </div>
  );
}
